// Step 1 — Taxpayer Information (Part I, Line 1)
import React, { useState, useEffect } from 'react';
import type { POAFormState, StepErrors } from '../../../types/poa';

interface Props {
  form: POAFormState;
  errors: StepErrors;
  onChange: (field: keyof POAFormState, value: string) => void;
}

function Field({
  label, required, error, hint, children,
}: { label: string; required?: boolean; error?: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 13, color: 'var(--muted)', marginBottom: 5 }}>
        {label}{required && <span style={{ color: 'var(--bad)', marginLeft: 3 }}>*</span>}
      </label>
      {children}
      {hint && !error && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>{hint}</div>}
      {error && <div style={{ fontSize: 12, color: 'var(--bad)', marginTop: 4 }}>{error}</div>}
    </div>
  );
}

const inputStyle = (hasError?: boolean): React.CSSProperties => ({
  width: '100%', padding: '8px 10px', borderRadius: 8,
  border: `1px solid ${hasError ? 'var(--bad)' : 'var(--border)'}`,
  background: 'rgba(255,255,255,.04)', color: 'var(--text)',
  fontSize: 14, outline: 'none', fontFamily: 'inherit',
  boxSizing: 'border-box',
});

// ── Auto-formatters ───────────────────────────────────────────────────────────

function formatEin(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 9);
  if (d.length <= 2) return d;
  return `${d.slice(0, 2)}-${d.slice(2)}`;
}

function formatSsn(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
}

export default function TaxpayerStep({ form, errors, onChange }: Props) {
  // Derive initial mode: explicit intent from prefill > value-based fallback > default SSN
  const [tinMode, setTinMode] = useState<'ein' | 'ssn'>(() => {
    if (form.taxpayerTinType === 'ein') return 'ein';
    if (form.taxpayerTinType === 'ssn') return 'ssn';
    return form.taxpayerEin.trim() ? 'ein' : 'ssn';
  });

  // Re-sync mode if prefill swaps the active field externally
  useEffect(() => {
    if (form.taxpayerTinType === 'ein') { setTinMode('ein'); return; }
    if (form.taxpayerTinType === 'ssn') { setTinMode('ssn'); return; }
    if (form.taxpayerEin.trim() && !form.taxpayerSsn.trim()) setTinMode('ein');
    if (form.taxpayerSsn.trim() && !form.taxpayerEin.trim()) setTinMode('ssn');
  }, [form.taxpayerTinType, form.taxpayerEin, form.taxpayerSsn]);

  function switchMode(next: 'ein' | 'ssn') {
    setTinMode(next);
    // Clear both fields when toggling so no stale value lingers
    onChange('taxpayerEin', '');
    onChange('taxpayerSsn', '');
    onChange('taxpayerTinType', next);
  }

  function handleTinChange(raw: string) {
    if (tinMode === 'ein') {
      onChange('taxpayerEin', formatEin(raw));
      onChange('taxpayerSsn', '');
    } else {
      onChange('taxpayerSsn', formatSsn(raw));
      onChange('taxpayerEin', '');
    }
  }

  const tinValue   = tinMode === 'ein' ? form.taxpayerEin : form.taxpayerSsn;
  const tinError   = tinMode === 'ein' ? errors.taxpayerEin : errors.taxpayerSsn;
  const tinErrorTin = errors.taxpayerTin; // "at least one required" error

  const e = errors;

  return (
    <div>
      <p style={{ color: 'var(--muted)', fontSize: 13, marginTop: 0 }}>
        Enter the taxpayer's information exactly as it appears on their tax return.
        A separate Form 2848 is required for each taxpayer.
      </p>

      <Field label="Taxpayer Name / Organization Name" required error={e.taxpayerName}>
        <input
          style={inputStyle(!!e.taxpayerName)}
          value={form.taxpayerName}
          placeholder="e.g. IRA PHILLIPS, INC. or DAVID S. MYERS"
          onChange={(ev) => onChange('taxpayerName', ev.target.value)}
        />
      </Field>

      <Field label="Taxpayer Address" required error={e.taxpayerAddress}>
        <textarea
          style={{ ...inputStyle(!!e.taxpayerAddress), resize: 'vertical', minHeight: 64 }}
          value={form.taxpayerAddress}
          placeholder="Street address, City, State, ZIP"
          onChange={(ev) => onChange('taxpayerAddress', ev.target.value)}
        />
      </Field>

      {/* Single TIN field with EIN / SSN toggle */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <span style={{ fontSize: 13, color: 'var(--muted)' }}>
            Taxpayer Identification Number<span style={{ color: 'var(--bad)', marginLeft: 3 }}>*</span>
          </span>
          <div style={{
            display: 'flex', borderRadius: 6, overflow: 'hidden',
            border: '1px solid var(--border)', fontSize: 12,
          }}>
            {(['ssn', 'ein'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => switchMode(mode)}
                style={{
                  padding: '3px 10px', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  fontWeight: tinMode === mode ? 700 : 400,
                  background: tinMode === mode ? 'rgba(122,162,255,.2)' : 'transparent',
                  color: tinMode === mode ? 'var(--accent)' : 'var(--muted)',
                  transition: 'background .15s',
                }}
              >
                {mode.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <input
          style={inputStyle(!!(tinError || tinErrorTin))}
          value={tinValue}
          placeholder={tinMode === 'ein' ? 'XX-XXXXXXX  (e.g. 63-0422062)' : 'XXX-XX-XXXX  (e.g. 448-58-8429)'}
          maxLength={tinMode === 'ein' ? 10 : 11}
          onChange={(ev) => handleTinChange(ev.target.value)}
        />

        {tinErrorTin && (
          <div style={{ fontSize: 12, color: 'var(--bad)', marginTop: 4 }}>{tinErrorTin}</div>
        )}
        {tinError && !tinErrorTin && (
          <div style={{ fontSize: 12, color: 'var(--bad)', marginTop: 4 }}>{tinError}</div>
        )}
        {!tinError && !tinErrorTin && (
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
            {tinMode === 'ein'
              ? 'Format: XX-XXXXXXX — required for businesses and entities'
              : 'Format: XXX-XX-XXXX — required for individuals'}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Field label="Daytime Phone Number" hint="Optional but recommended">
          <input
            style={inputStyle()}
            value={form.taxpayerPhone}
            placeholder="(205) 913-8783"
            onChange={(ev) => onChange('taxpayerPhone', ev.target.value)}
          />
        </Field>

        <Field label="Plan Number" hint="Only for retirement plan matters">
          <input
            style={inputStyle()}
            value={form.taxpayerPlanNumber}
            placeholder="e.g. 002"
            onChange={(ev) => onChange('taxpayerPlanNumber', ev.target.value)}
          />
        </Field>
      </div>

      <Field label="Taxpayer Email" hint="Required to send for client e-signature via SafeSend">
        <input
          style={inputStyle()}
          type="email"
          value={form.taxpayerEmail}
          placeholder="client@example.com"
          onChange={(ev) => onChange('taxpayerEmail', ev.target.value)}
        />
      </Field>
    </div>
  );
}
